import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FILTER_SIDEBAR, FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, SHADOW, SURFACE } from "../../../theme/tokens";
import SystemButton from "../SystemButton/SystemButton";

/** Content cross-fade — deliberately shorter than the width transition it sits inside. */
const RAIL_CROSSFADE_MS = Math.round(MOTION.DURATION.RAIL_TOGGLE * 0.45);
/** Entrance travel (px) for the `pill` variant's panel, which fades in rather than animating width. */
const PILL_ENTER_OFFSET_PX = 8;

type FilterSidebarProps = {
	/** Collapsed state is controlled so callers can persist it. */
	collapsed: boolean;
	/** Omit to make the sidebar permanently visible — the collapse chevron is then hidden. */
	onCollapsedChange?: (collapsed: boolean) => void;
	/** Number of active filters — shown on the collapsed "Filters (n)" pill and gates "Clear all". */
	activeFilterCount?: number;
	/** Renders a "Clear all" action in the header while `activeFilterCount > 0`. */
	onClearAll?: () => void;
	/** Pinned above the scrollable sections (e.g. a search field). */
	topSlot?: ReactNode;
	/** The filter sections (`FacetFilterSection`s). Separated by hairline dividers. */
	children: ReactNode;
	/** Panel width (px). Default: `FILTER_SIDEBAR.WIDTH` token. */
	width?: number;
	/** Already-translated panel title. Default: t("filter.sidebar.title"). */
	title?: string;
	/**
	 * How the panel renders when collapsed. `pill` (default) is a compact button that sits where the
	 * panel was — right for a panel inside a page section. `rail` is a full-height 44px strip with the
	 * title running vertically up it, which is what a page-level sidebar wants: the strip holds the
	 * column, so reopening it doesn't shift the table sideways, and the label stays readable.
	 */
	collapsedVariant?: "pill" | "rail";
	/** Collapsed pill shows only the filter icon (no "Filters (n)" label) — a smaller footprint for a
	 * narrower host layout. Default false: every existing consumer keeps the labeled pill unchanged.
	 * Ignored by `collapsedVariant="rail"`, whose strip always shows the icon plus a vertical label. */
	collapsedIconOnly?: boolean;
	/**
	 * How the panel is framed. `card` (default) draws its own border, radius and shadow — right for a
	 * panel floating in a page. `flush` draws only a divider on its inline-end edge, for when the
	 * PARENT frames the rail and the table together as one surface: two separately-framed panels with
	 * a gap between them read as two unrelated things, when the rail exists to drive the table.
	 */
	frame?: "card" | "flush";
	dataTestId?: string;
};

/**
 * Faceted "Filters" sidebar shell: header (title + active count + Clear all + optional
 * collapse chevron), an optional pinned top slot, and a scrollable stack of filter
 * sections. Collapse is opt-in — pass `onCollapsedChange` to get the chevron and the
 * compact "Filters (n)" pill that reopens the panel; omit it for a permanently visible
 * sidebar. Owns no filter logic — pages wire state through `FacetFilterSection`s.
 *
 * The panel and the collapsed pill are both always mounted (toggled via `display`, not conditional
 * JSX) — collapsing/expanding never unmounts the filter controls inside `children`, so a control-heavy
 * panel (many dropdowns/autocompletes) doesn't pay a fresh mount cost — and the resulting stutter —
 * on every expand.
 */
export const FilterSidebar: FC<FilterSidebarProps> = ({
	collapsed,
	onCollapsedChange,
	// DEFAULT: no count given = treat as no active filters, so the pill badge / Clear all stay hidden until a caller opts in.
	activeFilterCount = 0,
	onClearAll,
	topSlot,
	children,
	// DEFAULT: fall back to the shared sidebar-width token so every faceted sidebar lines up unless a caller overrides.
	width = FILTER_SIDEBAR.WIDTH,
	title,
	// DEFAULT: false — every existing consumer (Team, PbjModel's RoleFilterPanel) keeps showing the labeled pill unchanged.
	collapsedIconOnly = false,
	dataTestId,
	// DEFAULT: the pill — the shape this component shipped with, so existing panels keep their layout.
	collapsedVariant = "pill",
	// DEFAULT: self-framed, which is how every existing caller is laid out.
	frame = "card",
}) => {
	const { t } = useTranslation();
	const heading = title ?? t("filter.sidebar.title");
	const collapsedLabel = activeFilterCount > 0 ? t("filter.sidebar.filters_with_count", { count: activeFilterCount }) : t("filter.sidebar.filters");
	// In flush mode the parent owns the frame; the panel contributes only the divider against the table.
	const frameSx =
		frame === "flush"
			? { borderInlineEnd: `1px solid ${NEUTRAL[200]}` }
			: { border: `1px solid ${NEUTRAL[200]}`, borderRadius: "8px", boxShadow: SHADOW.PANEL_SOFT };

	const panelBody = (
		<>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, pt: 1.25, pb: 0.75 }}>
				<Typography component="h2" sx={{ flex: 1, fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: INK.PRIMARY }}>
					{heading}
					{activeFilterCount > 0 && <Box component="span" sx={{ color: "primary.main", ml: 0.75 }}>{`· ${activeFilterCount}`}</Box>}
				</Typography>
				{onClearAll && (
					<SystemButton
						buttonVariant="borderless"
						size="small"
						onClick={activeFilterCount > 0 ? onClearAll : undefined}
						noFullWidthMobile
						data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
						aria-hidden={activeFilterCount === 0 || undefined}
						sx={{
							fontSize: FONT_SIZE.SECONDARY,
							whiteSpace: "nowrap",
							minWidth: 0,
							px: 1,
							visibility: activeFilterCount > 0 ? "visible" : "hidden",
							pointerEvents: activeFilterCount > 0 ? undefined : "none",
						}}
					>
						{t("filter.sidebar.clear_all")}
					</SystemButton>
				)}
				{onCollapsedChange && (
					<IconButton
						size="small"
						onClick={() => onCollapsedChange(true)}
						aria-label={t("filter.sidebar.collapse")}
						aria-expanded
						data-testid={dataTestId ? `${dataTestId}-collapse` : undefined}
						sx={(theme) => ({ color: INK.MUTED, ...(theme.direction === "rtl" ? { transform: "scaleX(-1)" } : {}) })}
					>
						<ChevronLeftIcon sx={{ fontSize: 20 }} />
					</IconButton>
				)}
			</Box>

			{topSlot && <Box sx={{ px: 1.5, pb: 1.25 }}>{topSlot}</Box>}

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					px: 1.5,
					pb: 1,
					// Hairline divider between stacked sections.
					"& > * + *": { borderTop: `1px solid ${NEUTRAL[150]}` },
				}}
			>
				{children}
			</Box>
		</>
	);

	/**
	 * The rail variant is ONE element whose width animates, not two elements swapping.
	 *
	 * Animating the mount of a collapsed strip and the mount of an expanded panel — which is what this
	 * did first — cannot produce a collapse: the 264px panel leaves the layout in a single frame and
	 * the table snaps to its new width, however long the incoming element's fade runs. So the panel and
	 * the strip are layers inside a container that transitions `width`, and the table beside it reflows
	 * continuously because its flex sibling is genuinely changing size.
	 *
	 * `width` is not a compositor-only property, so this is the deliberate exception to the
	 * transform/opacity rule: a drawer that changes the page's layout has to animate layout. It is one
	 * element, once per click. Both layers stay mounted throughout, so `children` never remounts.
	 */
	if (collapsedVariant === "rail") {
		return (
			<Box
				component="aside"
				aria-label={heading}
				data-testid={dataTestId}
				sx={{
					width: collapsed ? FILTER_SIDEBAR.COLLAPSED_WIDTH : width,
					flexShrink: 0,
					minHeight: 0,
					position: "relative",
					display: "flex",
					flexDirection: "column",
					// Clips the fixed-width panel while the container narrows, so the content is revealed and
					// concealed rather than reflowing text on every frame of the transition.
					overflow: "hidden",
					backgroundColor: SURFACE,
					...frameSx,
					transition: `width ${MOTION.DURATION.RAIL_TOGGLE}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}}
			>
				<Box
					// Held at the expanded width so it doesn't squeeze; the container's overflow does the work.
					sx={{
						width,
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						opacity: collapsed ? 0 : 1,
						pointerEvents: collapsed ? "none" : "auto",
						// Faster than the width so the panel is out of the way before the strip arrives,
						// instead of the two layers being visible on top of each other mid-transition.
						transition: `opacity ${RAIL_CROSSFADE_MS}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					}}
					aria-hidden={collapsed || undefined}
					inert={collapsed || undefined}
				>
					{panelBody}
				</Box>

				<Box
					component="button"
					type="button"
					onClick={() => onCollapsedChange?.(false)}
					aria-expanded={false}
					aria-label={heading}
					data-testid={dataTestId ? `${dataTestId}-expand` : undefined}
					inert={!collapsed || undefined}
					sx={{
						position: "absolute",
						inset: 0,
						width: FILTER_SIDEBAR.COLLAPSED_WIDTH,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 1.25,
						py: 1.75,
						border: 0,
						backgroundColor: "transparent",
						cursor: "pointer",
						color: INK.MUTED,
						opacity: collapsed ? 1 : 0,
						pointerEvents: collapsed ? "auto" : "none",
						transition: `opacity ${RAIL_CROSSFADE_MS}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { color: INK.PRIMARY },
						},
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					}}
				>
					<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
					{activeFilterCount > 0 && (
						<Box
							sx={{
								minWidth: BADGE.PILL_SIZE,
								height: BADGE.PILL_SIZE,
								px: 0.5,
								borderRadius: BADGE.PILL_RADIUS_FULL,
								bgcolor: "primary.main",
								color: "primary.contrastText",
								fontSize: FONT_SIZE.LABEL_MICRO,
								fontWeight: 700,
								display: "grid",
								placeItems: "center",
							}}
						>
							{activeFilterCount}
						</Box>
					)}
					{/* Vertical so the label survives a 44px column instead of being cropped to "Fil…". */}
					<Typography
						sx={{
							writingMode: "vertical-rl",
							fontSize: FONT_SIZE.LABEL_XS,
							fontWeight: 600,
							letterSpacing: LETTER_SPACING.LABEL,
							textTransform: "uppercase",
							whiteSpace: "nowrap",
						}}
					>
						{heading}
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ flexShrink: 0, alignSelf: "flex-start", display: collapsed ? "block" : "none" }}>
				<SystemButton
					buttonVariant="secondary"
					size="small"
					onClick={() => onCollapsedChange?.(false)}
					leadingIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
					iconButton={collapsedIconOnly}
					noFullWidthMobile
					aria-expanded={false}
					aria-label={collapsedIconOnly ? collapsedLabel : undefined}
					tooltip={collapsedIconOnly ? collapsedLabel : undefined}
					data-testid={dataTestId ? `${dataTestId}-expand` : undefined}
					sx={{ borderColor: NEUTRAL[300], whiteSpace: "nowrap" }}
				>
					{collapsedIconOnly ? undefined : collapsedLabel}
				</SystemButton>
			</Box>

			<Box
				component="aside"
				aria-label={heading}
				data-testid={dataTestId}
				sx={(theme) => ({
					display: collapsed ? "none" : "flex",
					width,
					flexShrink: 0,
					minHeight: 0,
					flexDirection: "column",
					backgroundColor: SURFACE,
					...frameSx,
					// The `pill` variant swaps panel for button, so its only motion is this entrance — kept at
					// its original short duration and small offset. Replays on every collapse -> expand toggle
					// (a `display: none` box restarts its CSS animation on becoming visible again). The `rail`
					// variant above animates width instead and never reaches here.
					"@keyframes filterSidebarIn": {
						from: { opacity: 0, transform: `translateX(${theme.direction === "rtl" ? PILL_ENTER_OFFSET_PX : -PILL_ENTER_OFFSET_PX}px)` },
						to: { opacity: 1, transform: "translateX(0)" },
					},
					animation: collapsed ? "none" : `filterSidebarIn ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { animation: "none" },
				})}
			>
				{panelBody}
			</Box>
		</>
	);
};
